import { env } from "@/env";
import axios, { type AxiosError } from "axios";

export class ApiError extends Error {
  status: number;
  /** Structured FastAPI `detail` payload when the backend emits one
   *  (e.g. `{code, message, details}` from the simulation re-run
   *  route). `undefined` for routes that emit a plain string detail —
   *  in that case the parsed message is on `.message` as usual. */
  detail?: Record<string, unknown>;
  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.status = status;
    this.name = "ApiError";
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      this.detail = detail as Record<string, unknown>;
    }
  }
}

const apiClient = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  withCredentials: true, // Send session cookies
  headers: { "Content-Type": "application/json" },
});

// Pages that may legitimately receive 401s without needing to bounce the
// user — `/` and `/login` both probe `/auth/me` to detect existing sessions
// for already-signed-in users, and a 401 there is the normal "guest" path.
const PUBLIC_PATHS = new Set(["/", "/login", "/auth/login"]);

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status ?? 0;
    // FastAPI emits `detail` as either a plain string (legacy) or a
    // structured object (e.g. `{code, message, details}` from the
    // simulation re-run route). Render the message slot as a string
    // for backwards-compat, but forward the structured object on
    // `ApiError.detail` so callers can pattern-match on `.code`.
    const rawDetail = (error.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    let messageStr: string;
    let structuredDetail: unknown = undefined;
    if (typeof rawDetail === "string") {
      messageStr = rawDetail;
    } else if (rawDetail && typeof rawDetail === "object") {
      messageStr =
        (rawDetail as { message?: unknown }).message?.toString() ??
        error.message ??
        "An unexpected error occurred.";
      structuredDetail = rawDetail;
    } else {
      messageStr = error.message ?? "An unexpected error occurred.";
    }

    if (status === 401) {
      const requestUrl = error.config?.url ?? "";
      const onPublicPage =
        typeof window !== "undefined" &&
        PUBLIC_PATHS.has(window.location.pathname);
      // The auth probe itself is allowed to 401 — that's how we detect guest
      // state. Same for any 401 received while the user is already on a
      // public page (would otherwise create a refresh loop).
      const isAuthProbe = requestUrl.includes("/auth/me");

      if (!isAuthProbe && !onPublicPage) {
        window.location.href = "/login";
      }
    }

    return Promise.reject(new ApiError(messageStr, status, structuredDetail));
  },
);

export default apiClient;
